/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1720656000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Jul 11 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "beeswax",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing segments",
          "depth": 0,
          "id": "syncing-segments"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Select Beeswax sync options",
          "depth": 1,
          "id": "select-beeswax-sync-options"
        }, {
          "value": "Record mappings",
          "depth": 1,
          "id": "record-mappings"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      em: "em",
      code: "code",
      a: "a",
      img: "img",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["After selecting Beeswax from our destinations catalog, you will be required to enter your ", _jsx(_components.em, {
          children: "Beeswax Subdomain"
        }), ",\n", _jsx(_components.em, {
          children: "Email"
        }), ", and ", _jsx(_components.em, {
          children: "Password"
        }), " from your Beeswax account. Your ", _jsx(_components.em, {
          children: "Beeswax Subdomain"
        }), " is the text before ", _jsx(_components.code, {
          children: ".beeswax.com"
        }), " in\nthe URL, ", _jsx(_components.em, {
          children: "Username"
        }), " and ", _jsx(_components.em, {
          children: "Password"
        }), " are the credentials you use to login to Beeswax."]
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally you can also add a salt that is provided by Beeswax that will be used when hashing data used for ", _jsx(_components.a, {
          href: "#hem",
          children: "HEM"
        }), " identifiers."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-segments",
        children: "Syncing segments"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports automatically creating a new segment for your sync. You can optionally assign a custom name to your segment.\nIf no name is provided, Hightouch will default to using your model name."
      }), "\n", _jsxs(_components.p, {
        children: ["Here, the model name ", _jsx(_components.code, {
          children: "User Segment 1"
        }), " will be used as the segment name:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-beeswax-create-segment.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Additionally, instead of creating a new segment, Hightouch can also sync users to existing segments that are already populated in your Beeswax account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-beeswax-existing-segment.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports Segment mode. New records will be inserted into a Beeswax segment. Records that are removed from the model will be removed from the\nsegment."
      }), "\n", _jsx(_components.h3, {
        id: "select-beeswax-sync-options",
        children: "Select Beeswax sync options"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to choose which Beeswax account and data center you wish to sync data to. This can be selected on the sync configuration page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-beeswax-account-options.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-mappings",
        children: "Record mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to your segment by ", _jsx(_components.code, {
          children: "Beeswax ID"
        }), ", ", _jsx(_components.code, {
          children: "Customer ID"
        }), ", ", _jsx(_components.code, {
          children: "HEM"
        }), ", ", _jsx(_components.code, {
          children: "IDFA"
        }), ", ", _jsx(_components.code, {
          children: "IDFA_MD5"
        }), ", ", _jsx(_components.code, {
          children: "IDFA_SHA1"
        }), ", ", _jsx(_components.code, {
          children: "AD_ID"
        }), ", ", _jsx(_components.code, {
          children: "AD_ID_MD5"
        }), ", ", _jsx(_components.code, {
          children: "AD_ID_SHA1"
        }), ", or ", _jsx(_components.code, {
          children: "IP_ADDRESS"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-beeswax-record-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "hem",
        children: "HEM"
      }), "\n", _jsxs(_components.p, {
        children: ["When using HEM as the matching type, the mapped value will automatically be hashed based on the ", _jsx(_components.a, {
          href: "https://hub.freewheel.tv/display/BW/Hashed+Email+Addresses+in+FreeWheel+Identity+Network",
          children: "docs"
        }), ".\nYou can turn this off using the checkbox if your IDs are already hashed."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-beeswax-toggle-hash.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
